import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { Link, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import useUrlParams from 'hooks/useUrlParams';
import { EisRuleDetailsProvider, useEisRuleDetailsContext } from '../context';
import RuleDetails from './tabs/RuleDetails';

export type EisRuleTab = {
  label: React.ReactNode;
  key: string;
  component: React.ReactNode;
  count?: number;
};

export type EisRuleUrlParams = {
  tab: string;
};

const defaultParams: EisRuleUrlParams = {
  tab: 'details',
};

const EisRuleDetails: React.FC = () => {
  const { rule } = useEisRuleDetailsContext();
  const [params, setParams] = useUrlParams(defaultParams);

  const tabs: { [key: string]: EisRuleTab } = React.useMemo(() => {
    const allTabs: { [key: string]: EisRuleTab } = {
      details: {
        label: t('performanceReporting.eis.details.tabs.details'),
        key: 'details',
        component: <RuleDetails />,
      },
    };

    return allTabs;
  }, [t]);

  const updateParams = React.useCallback(
    (updatedParams: EisRuleUrlParams) => {
      setParams((prev: EisRuleUrlParams) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const activeTab = React.useMemo(() => {
    return tabs[params.tab] ? params.tab : 'details';
  }, [tabs, params.tab]);

  const headerTitle = rule?.name || '';

  const breadcrumbItems = React.useMemo(() => {
    const items = [
      <Link
        key='b1'
        href='/admin/performance-reporting/early-intervention-system'
      >
        {t('performanceReporting.eis.list.title')}
      </Link>,
    ];

    // Add current rule name
    items.push(<Typography key='b2'>{rule?.name || ''}</Typography>);

    return items;
  }, [rule?.name, t]);

  return (
    <CommonLayout
      headerProps={{
        title: headerTitle,
        breadcrumbItems,
        actions: null,
      }}
      tabs={{
        items: Object.entries(tabs).map(([key, tab]) => ({
          label: tab.label,
          value: key,
          count: tab.count,
        })),
        value: activeTab,
        onChange: (event: React.SyntheticEvent, newValue: string | number) =>
          updateParams({ tab: newValue.toString() }),
      }}
    >
      {tabs[activeTab]?.component}
    </CommonLayout>
  );
};

const EisRuleDetailsWrapper: React.FC = () => {
  return (
    <EisRuleDetailsProvider>
      <Routes>
        <Route
          path='*'
          element={<EisRuleDetails />}
        />
      </Routes>
    </EisRuleDetailsProvider>
  );
};

export default EisRuleDetailsWrapper;

